import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { CheckCircle2, AlertCircle, Briefcase, ArrowRight, LogIn } from 'lucide-react';
import api from '../services/api';

const AcceptInvitation = () => {
  const { token } = useParams();
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const { refreshProjects } = useProject();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const res = await api.get(`/invitations/${token}`);
        setInvitation(res.data);
      } catch (err) {
        const apiError = err.response?.data?.error;
        setError(apiError || t('invalidInvitation'));
      } finally {
        setLoading(false);
      }
    };
    fetchInvitation();
  }, [token]);

  // Auto-redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user && !loading && !error) {
      navigate(`/login?redirectTo=${encodeURIComponent(window.location.pathname)}`);
    }
  }, [authLoading, user, loading, error, navigate]);

  // Auto-accept ONLY if user is logged in
  useEffect(() => {
    if (user && invitation && !processing && !success && !error) {
      handleAccept();
    }
  }, [user, invitation, processing, success, error]);

  const handleAccept = async () => {
    if (!user) {
      navigate(`/login?redirectTo=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    setProcessing(true);
    try {
      await api.post('/invitations/accept', { token });
      await refreshProjects(); // Sync sidebar and access immediately
      setSuccess(true);
      setTimeout(() => {
        navigate('/projects');
      }, 3000);
    } catch (err) {
      const apiError = err.response?.data?.error;
      setError(apiError || t('errorInvitingMember'));
      setProcessing(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="glass-card p-10 max-w-md border-rose-500/20"
        >
          <div className="w-16 h-16 rounded-full bg-rose-500/10 flex items-center justify-center text-rose-500 mx-auto mb-6">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-2xl font-display font-black text-white mb-4">{t('invitationError')}</h2>
          <p className="text-slate-500 mb-8">
            {error?.toString().toLowerCase().includes('different email') || error?.toString().toLowerCase().includes('diferente')
              ? t('invitationEmailMismatch').replace('{email}', invitation?.email).replace('{currentUser}', user?.email)
              : error?.toString().toLowerCase().includes('already a member') || error?.toString().toLowerCase().includes('ya es miembro') 
                ? t('alreadyMember') 
                : error}
          </p>

          <div className="flex flex-col gap-3">
            {(error?.toString().toLowerCase().includes('different email') || error?.toString().toLowerCase().includes('diferente')) && (
               <button
                onClick={() => navigate(`/login?redirectTo=${encodeURIComponent(window.location.pathname)}&prompt=select_account`)}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-2"
              >
                <LogIn size={18} />
                {t('switchAccount')}
              </button>
            )}
            
            <button
              onClick={() => navigate('/')}
              className="w-full py-4 bg-white/5 hover:bg-white/10 text-white rounded-2xl font-bold transition-all border border-white/5"
            >
              {t('understood')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="glass-card p-12 max-w-md border-emerald-500/20"
        >
          <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500 mx-auto mb-6 shadow-[0_0_30px_rgba(16,185,129,0.2)]">
            <CheckCircle2 size={40} />
          </div>
          <h2 className="text-3xl font-display font-black text-white mb-4">{t('invitationAccepted')}</h2>
          <p className="text-slate-500 mb-8">{t('redirectingToProjects')}</p>
          <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 3 }}
              className="h-full bg-emerald-500"
            />
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="glass-card p-10 max-w-lg w-full rounded-[32px] border-primary/20 relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-primary/10 blur-3xl rounded-full" />

        <div className="relative z-10 text-center">
          <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-8 shadow-xl">
            <Briefcase size={36} />
          </div>

          <h1 className="text-3xl font-display font-black text-white mb-2">{t('invitationTitle')}</h1>
          <p className="text-slate-400 mb-8">
            {t('invitationDesc')} <span className="text-primary font-bold">"{invitation?.projectId?.name}"</span>
          </p>

          {!user ? (
            <div className="space-y-6">
              <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-amber-500 text-sm font-medium">
                {t('loginToAccept')}
              </div>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-2 group"
              >
                <LogIn size={18} />
                {t('loginToViewGate')}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/5 border border-white/5 text-left">
                <div className="w-10 h-10 rounded-full overflow-hidden border border-white/10">
                  <img src={user.avatar} alt={user.name} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">{t('joiningAs')}</div>
                  <div className="text-sm font-bold text-slate-200">{user.email}</div>
                </div>
              </div>

              <button
                onClick={handleAccept}
                disabled={processing}
                className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-2 group disabled:opacity-50"
              >
                {processing ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {t('joinProject')}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AcceptInvitation;
